import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { AuthenticationInitialState } from '@Features/Authentication/AuthenticationSlice/Authentication.slice.types'
import {
	LoginRequestPayload,
	CompleteProfileRequestPayload,
	VerifyPhoneRequestPayload,
	VerifyPhoneResponsePayload, AuthUserData
} from '@Features/Authentication/AuthenticationApi/Authentication.api.types'
import AuthenticationApi from '@Features/Authentication/AuthenticationApi/Authentication.api'
import LocalStorageKeys from '@Constants/LocalStorageKeys'
import { SignupRequestPayload } from '../AuthenticationApi/Authentication.api.types'
import { BaseApi } from '../../Base/BaseApi/BaseApi'
import { resetUnauthorizedGuard } from '../../Base/BaseApi/handleUnauthorized'
import { setCsrfToken, clearCsrfToken } from '@Api/csrfToken'

// export const setAuthData = createAction<AuthUserData, string>('auth/auth-obj')
// Partial: most callers pass only `redirect` to steer the post-enrollment flow.
// Typing it as the full AuthUserData forced an `as any` at every one of those
// sites — casts at exactly the places a shape change needed to be caught.
export const setAuthData = createAsyncThunk('auth/auth-obj', (authObj: Partial<AuthUserData>, { rejectWithValue }) => {
	try {
		return Promise.resolve(authObj)
	} catch (error) {
		return rejectWithValue(error)
	}
})

/**
 * Resolve session state from the server on boot.
 *
 * Returns null for an anonymous visitor rather than rejecting, so a 401 here is
 * an expected answer ("not signed in") and never triggers the terminal
 * unauthorized redirect — which would put a logged-out visitor into a login
 * loop on the public landing page.
 */
export const bootstrapAuthAction = createAsyncThunk('auth/bootstrap', async (options?: { quiet?: boolean }) => {
	try {
		const { data } = await AuthenticationApi.me()
		const deviceUnverified = Boolean(data?.data?.deviceUnverified)
		// The only way the portal can obtain this — it is not readable from a
		// cookie, because the API is on a different host.
		setCsrfToken(data?.data?.csrfToken)
		return {
			user: data?.data?.user ?? null,
			deviceUnverified,
			// The outstanding onboarding step. `checkAuthAction` used to rebuild
			// this from the localStorage blob on every boot; with the blob gone it
			// has to come from the server, so /auth/me now reports it via the same
			// checkRedirection the dedicated endpoint uses.
			redirect: data?.data?.redirect ?? null,
			// Widget identity rides the same boot answer; null means don't inject.
			chat: data?.data?.chat ?? null,
		}
	} catch (error) {
		// Quiet revalidation (bfcache restore / tab refocus): an unreachable server
		// is not "signed out". Downgrading a live session over a network blip would
		// log a student out mid-lesson. Only a 401 — the server actually answering
		// "not signed in" — falls through to the anonymous result below.
		const status = (error as { response?: { status?: number } })?.response?.status
		if (options?.quiet && status !== 401) return null
		return { user: null, deviceUnverified: false, redirect: null, chat: null }
	}
})

/**
 * End the session server-side, then clear local state.
 *
 * The server call comes first and its failure is swallowed: if it fails the
 * user still expects to be signed out locally, and the session will expire on
 * its own. Leaving them apparently signed in would be worse.
 */
export const logoutRequestAction = createAsyncThunk('auth/logout-request', async (_, { dispatch }) => {
	try {
		await AuthenticationApi.logout()
	} catch (error) {
		// best-effort
	}
	dispatch(authenticationSlice.actions.logoutAction())
})

/**
 * Admin impersonation handoff: exchange a one-time code for a session.
 *
 * Only reached inside the admin portal's embed. Failure is silent by design —
 * the code is single-use, so a refresh of the iframe replays a spent code and
 * an error toast there would be noise, not information.
 */
export const redeemImpersonationAction = createAsyncThunk('auth/impersonate-redeem', async (code: string) => {
	const { data } = await AuthenticationApi.redeemImpersonation(code)
	setCsrfToken(data?.data?.csrfToken)
	return data?.data?.user ?? null
})

/**
 * Codes the server returns in `data.redirect` to say what the student must do
 * before the app is usable. Mirrors Codes.Error.Redirect in the backend.
 */
export const RedirectCodes = {
	PHONE_VERIFICATION: 101,
	COMPLETE_PROFILE: 103,
	REGISTRATION_PAYMENT: 104,
	/** Signed in, but on a browser this account has not been seen on before. */
	DEVICE_VERIFICATION: 105,
} as const

// async thunks
export const loginAction = createAsyncThunk('auth/login', async (authObject: LoginRequestPayload, { rejectWithValue }) => {
	try {
		const { data } = await AuthenticationApi.login(authObject)
		setCsrfToken(data?.data?.csrfToken)
		return data
	} catch (error) {
		return rejectWithValue(error)
	}
})
export const signupAction = createAsyncThunk('auth/signup', async (form: SignupRequestPayload, { rejectWithValue }) => {
	try {
		const { data } = await AuthenticationApi.signup(form)
		setCsrfToken(data?.data?.csrfToken)
		return data
	} catch (error) {
		return rejectWithValue(error)
	}
})

export const verifyCodeAction = createAsyncThunk('auth/verify-phone', async (form: VerifyPhoneRequestPayload) => {
	const { data } = await AuthenticationApi.verifyPhone(form)
	return data
})

export const completeProfileAction = createAsyncThunk(
	'auth/complete-profile',
	async (completeProfilePayload: CompleteProfileRequestPayload) => {
		const { data } = await AuthenticationApi.completeProfile(completeProfilePayload)
		return data
	}
)

const initialState: AuthenticationInitialState = {
	isAuth: false,
	authReady: false,
	deviceUnverified: false,
	isProfileCompleted: false,
	user: null,
	redirect: null,
	chat: null,
	isAuthSignup: false,
	needRedirect: false,
	settingAuthDataCompleted: null,
	loaders: {
		login: 'idle',
		profile: 'idle',
		signup: 'idle',
	},
}

const authenticationSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
		/**
		 * Session state is now the server's answer, not a guess from localStorage.
		 *
		 * This used to read a JSON blob out of localStorage and set isAuth=true if
		 * it merely EXISTED — never validated. So the app rendered as signed in
		 * against a token the server had already rejected, and only discovered
		 * otherwise when some request happened to 401.
		 *
		 * The credential is an httpOnly cookie now and cannot be read here at all,
		 * which is the point. `bootstrapAuthAction` asks /auth/me instead.
		 */
		checkAuthAction: (state: any) => {
			// Retained as a no-op entry point so existing callers keep working;
			// the real resolution happens in bootstrapAuthAction below.
			state.isAuth = state.isAuth === true
		},
		/**
		 * Clears LOCAL state only.
		 *
		 * Ending the session server-side is `logoutRequestAction` below — this
		 * reducer is also what runs after a 401, where there is no session left
		 * to end. Previously this was the entire logout: localStorage was cleared
		 * while the token stayed valid on the server until it expired, which
		 * (with expiresIn commented out) was never.
		 */
		/** The device passed its challenge; everything it was gated from is open now. */
		deviceVerifiedAction: (state: any) => {
			state.deviceUnverified = false
		},
		logoutAction: (state: any) => {
			clearCsrfToken()
			state.deviceUnverified = false
			localStorage.removeItem(LocalStorageKeys.AUTH)
			state.isAuth = false
			state.user = null
			// The widget teardown keys off this going null — without it the next
			// account on this browser inherits the previous student's chat.
			state.chat = null
			state.loaders.login = 'idle'
			state.needRedirect = false
			state.redirect = undefined
		},
	},
	extraReducers: builder => {
		// login
		builder
			.addCase(loginAction.pending, state => {
				state.loaders.login = 'pending'
			})
			.addCase(loginAction.fulfilled, (state, { payload: { data } }) => {
				// A fresh sign-in retires the previous expiry, so a later one on the
				// same path is not swallowed by the once-per-path guard.
				resetUnauthorizedGuard()
				state.deviceUnverified = data.redirect === RedirectCodes.DEVICE_VERIFICATION
				state.user = data.user
				state.redirect = data.redirect
				// Same contract as /auth/me: the widget can mount right after login.
				state.chat = data.chat ?? null
				// No localStorage write: the session lives in an httpOnly cookie the
				// server has already set on this response. Mirroring it here is what
				// made a stale blob look like a live session.
				state.isAuth = true
				state.authReady = true
				state.loaders.login = 'fulfilled'
			})
			.addCase(loginAction.rejected, state => {
				state.loaders.login = 'rejected'
			})
		//signup
		builder
			.addCase(signupAction.pending, state => {
				state.loaders.signup = 'pending'
			})
			.addCase(signupAction.fulfilled, (state, { payload: { data } }) => {
				state.user = data.user
				state.isAuth = true
				state.authReady = true
				state.isAuthSignup = true
				state.loaders.login = 'fulfilled'
				state.redirect = data.redirect
			})
			.addCase(signupAction.rejected, state => {
				state.loaders.signup = 'rejected'
			})

		builder.addCase(setAuthData.pending, state => {
			state.settingAuthDataCompleted = null
		})
		builder.addCase(setAuthData.fulfilled, (state, action) => {
			state.settingAuthDataCompleted = false
			const { user, redirect } = action.payload
			state.redirect = redirect
			state.needRedirect = redirect ? true : false
			// Several callers dispatch this with only a `redirect` to steer the
			// post-enrollment flow. Those must not be read as a sign-in, and must
			// not pre-empt bootstrapAuthAction's answer.
			if (user) {
				state.user = user
				state.isAuth = true
				state.authReady = true
			}
			state.settingAuthDataCompleted = true
		})

		// Boot: /auth/me is the only thing that can answer "is this session live".
		// authReady exists so the UI can tell "not signed in" apart from "haven't
		// asked yet" — without it every guarded view flashes its logged-out state
		// on load, and anything that redirects on !isAuth bounces a valid session
		// to /login before the answer arrives.
		builder
			.addCase(bootstrapAuthAction.pending, (state, action) => {
				// Quiet revalidations keep authReady true. Flipping it would unmount
				// every authReady-gated subtree — a student mid-video would lose the
				// player just because the tab regained focus.
				if (!action.meta.arg?.quiet) state.authReady = false
			})
			.addCase(bootstrapAuthAction.fulfilled, (state, action) => {
				// null = a quiet revalidation that got no answer from the server; the
				// state we already have is better than guessing.
				if (action.payload === null) return
				state.user = action.payload.user
				state.isAuth = Boolean(action.payload.user)
				state.deviceUnverified = action.payload.deviceUnverified
				state.chat = action.payload.chat ?? null
				// 105 wins when the device is pending: nothing else is reachable
				// until the challenge is passed, and the server reports that state
				// through `deviceUnverified` rather than through the redirect code.
				// Otherwise take whatever step the server says is outstanding — 101
				// phone, 103 profile, 104 registration fees.
				const restored = action.payload.deviceUnverified ? RedirectCodes.DEVICE_VERIFICATION : action.payload.redirect
				state.redirect = restored ?? undefined
				state.needRedirect = Boolean(restored)
				state.authReady = true
			})
			.addCase(bootstrapAuthAction.rejected, state => {
				// The thunk swallows its own errors, so this only fires on a
				// programming fault. Treat it as anonymous rather than hanging.
				state.user = null
				state.isAuth = false
				state.authReady = true
			})

		builder
			.addCase(redeemImpersonationAction.fulfilled, (state, action) => {
				state.user = action.payload
				state.isAuth = Boolean(action.payload)
				state.authReady = true
			})
			.addCase(redeemImpersonationAction.rejected, state => {
				// Spent, expired or forged code. Leave whatever bootstrap decided.
				state.authReady = true
			})
	},
})

export const { logoutAction, checkAuthAction, deviceVerifiedAction } = authenticationSlice.actions
export default authenticationSlice.reducer

export const AuthApi = BaseApi.injectEndpoints({
	endpoints: builder => ({
		verifyPhone: builder.query<VerifyPhoneResponsePayload, VerifyPhoneRequestPayload>({
			query: payload => {
				return { url: `/auth/phone/confirm`, method: 'POST', body: payload }
			},
		}),
		completeProfile: builder.query<ApiResponse<AuthUserData>, CompleteProfileForm>({
			query: (payload: CompleteProfileForm) => {
				return { url: '/student/profile/complete', method: 'PUT', body: payload }
			},
		}),
		forgetPassword: builder.query<ApiResponse<any>, ForgetPasswordPayload>({
			query: payload => {
				return { url: '/auth/password/forget', method: 'POST', body: payload }
			},
		}),
		resetPassword: builder.query<ApiResponse<any>, ResetPasswordPayload>({
			query: payload => ({ url: '/auth/password/reset', method: 'POST', body: payload }),
		}),
		resendPhone: builder.query<ApiResponse<any>, string>({
			query: payload => ({ url: '/auth/phone/verification/resend', method: 'POST', body: { phone_number: payload } }),
		}),
		// Correct a mistyped number without abandoning the account. The server
		// only allows this while phone_verified is false, and issuing the new code
		// supersedes the one sent to the old number.
		changePhone: builder.query<ApiResponse<any>, string>({
			query: payload => ({ url: '/auth/phone/change', method: 'POST', body: { phone_number: payload } }),
		}),
		resendOTP: builder.query<ApiResponse<any>, string>({
			query: payload => ({ url: '/auth/password/verification/resend', method: 'POST', body: { identifier: payload } }),
		}),
		checkPasswordVerification: builder.query<ApiResponse<any>, CheckResetCodePayload>({
			query: payload => ({ url: '/auth/password/verification/check', method: 'POST', body: payload }),
		}),
	}),
})

export const {
	useLazyVerifyPhoneQuery,
	useLazyCompleteProfileQuery,
	useLazyForgetPasswordQuery,
	useLazyResetPasswordQuery,
	useLazyResendPhoneQuery,
	useLazyChangePhoneQuery,
	useLazyResendOTPQuery,
	useLazyCheckPasswordVerificationQuery,
} = AuthApi
